/**
 * HOW LONG IS THIS FILE? Answered in the browser, before the bytes are sent.
 *
 * `POST /admin/recordings` requires `durationSec` and the server cannot supply
 * it: the audio has not been uploaded yet, and the manager never decodes audio
 * at all — that is the worker's job, and the worker runs minutes later. So the
 * page that has the file in its hands measures it.
 *
 * IT MATTERS THAT THIS IS RIGHT-ISH, not that it is exact. The worker
 * overwrites `duration_sec` with the DECODED length once it processes the
 * recording (`finishRecording`), because the tiling invariant is checked
 * against that number and nothing else may disagree with it. What this value
 * does in the meantime is drive the list's "3m 41s" and the server's sanity
 * bound. A file whose duration cannot be read is still uploadable.
 *
 * TWO READERS, CHEAPEST FIRST:
 *
 *   1. `<audio>` metadata. Costs a header parse. This is the R9 exception the
 *      brief grants — that rule governs `src/`, the browser PWA, where all
 *      audio must go through `audioBus` so two clips can never play at once.
 *      Nothing here ever plays a sound; the element is a decoder that is
 *      thrown away, and there is no bus in this app to route around.
 *   2. `decodeAudioData`. Costs decoding the WHOLE file into memory. Only
 *      reached when (1) returns `Infinity` or `NaN`, which is what a streamed
 *      WebM/Matroska container with no duration in its header does — the
 *      common shape of a browser recording, so this path is not theoretical.
 */

/** Nine hours, matching `MAX_DURATION_SEC` in `routes/recordings.ts`. Past
 *  this the server would refuse the create with a 400, so the guess is
 *  clamped rather than sent to be rejected. */
const MAX_DURATION_SEC = 9 * 60 * 60;

/** How long to wait for a metadata event before deciding it is not coming. */
const METADATA_TIMEOUT_MS = 15_000;

export async function readDurationSec(file: File): Promise<number> {
  const quick = await fromAudioElement(file);
  if (quick !== null) return clamp(quick);

  const decoded = await fromDecode(file);
  if (decoded !== null) return clamp(decoded);

  /*
   * ZERO, and the upload continues.
   *
   * The alternative is refusing to upload a file we could not measure, which
   * would block a perfectly good recording over a number the worker is about
   * to replace anyway. `0` is inside the server's accepted range and reads
   * honestly in the list as an unknown length.
   */
  return 0;
}

function clamp(seconds: number): number {
  if (!Number.isFinite(seconds) || seconds < 0) return 0;
  return Math.min(seconds, MAX_DURATION_SEC);
}

/** `null` when this reader cannot answer, so the caller tries the next one. */
function fromAudioElement(file: File): Promise<number | null> {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const el = document.createElement('audio');
    el.preload = 'metadata';

    let settled = false;
    const finish = (value: number | null) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timer);
      // Revoking is not housekeeping: each object URL pins the whole file in
      // memory until it is released, and an admin queueing eight sessions
      // would otherwise hold all eight.
      URL.revokeObjectURL(url);
      el.removeAttribute('src');
      el.load();
      resolve(value);
    };

    const timer = window.setTimeout(() => finish(null), METADATA_TIMEOUT_MS);

    el.onloadedmetadata = () => {
      const d = el.duration;
      finish(Number.isFinite(d) && d > 0 ? d : null);
    };
    el.onerror = () => finish(null);

    el.src = url;
  });
}

async function fromDecode(file: File): Promise<number | null> {
  const Ctor = window.AudioContext ?? (window as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!Ctor) return null;

  const ctx = new Ctor();
  try {
    const bytes = await file.arrayBuffer();
    const buffer = await ctx.decodeAudioData(bytes);
    return buffer.duration > 0 ? buffer.duration : null;
  } catch {
    return null;
  } finally {
    // An AudioContext left open holds an audio hardware handle, and browsers
    // cap how many a page may have — six failed reads and the seventh throws.
    void ctx.close().catch(() => {});
  }
}

/* ------------------------------------------------------------------ */
/* Formatting, kept next to the measuring                              */
/* ------------------------------------------------------------------ */

export function formatDuration(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds <= 0) return '—';
  const total = Math.round(seconds);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  if (h > 0) return `${h}h ${String(m).padStart(2, '0')}m`;
  if (m > 0) return `${m}m ${String(s).padStart(2, '0')}s`;
  return `${s}s`;
}

export function formatBytes(bytes: number | null): string {
  if (bytes === null || !Number.isFinite(bytes) || bytes <= 0) return '—';
  if (bytes < 1024) return `${bytes} B`;
  const units = ['KB', 'MB', 'GB'];
  let value = bytes / 1024;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${value < 10 ? value.toFixed(1) : Math.round(value)} ${units[unit]}`;
}

export function formatWhen(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const now = Date.now();
  const diffSec = Math.round((now - d.getTime()) / 1000);
  if (diffSec < 60) return 'just now';
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ago`;
  if (diffSec < 86_400) return `${Math.floor(diffSec / 3600)}h ago`;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}
